import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export interface MultiSelectDropdownProps {
  label: React.ReactNode;
  placeholder: string;
  options: string[];
  optionCounts?: Record<string, number>;
  allOptionCount?: number;
  optionDescriptions?: Record<string, string>;
  disabledOptions?: string[];
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  searchable?: boolean;
  searchLabel?: string;
  showAllOption?: boolean;
  layout?: 'stacked' | 'inline';
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  placeholder,
  options,
  optionCounts,
  allOptionCount,
  optionDescriptions,
  disabledOptions = [],
  values,
  onChange,
  disabled = false,
  searchable = false,
  searchLabel,
  showAllOption = true,
  layout = 'stacked',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const menuId = useId();
  const selected = useMemo(() => new Set(values), [values]);
  const disabledOptionSet = useMemo(() => new Set(disabledOptions), [disabledOptions]);

  useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      return;
    }

    if (searchable) {
      searchInputRef.current?.focus();
    }
  }, [open, searchable]);

  const toggleValue = (value: string) => {
    const next = new Set(values);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    const updated = Array.from(next);
    onChange(updated);
  };

  const clearValues = () => {
    onChange([]);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
    triggerRef.current?.focus();
  };

  const displayText = useMemo(() => {
    if (values.length === 0) return placeholder;
    if (values.length <= 2) return values.join(', ');
    return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
  }, [placeholder, values]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options;
    const needle = searchTerm.toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(needle));
  }, [options, searchTerm, searchable]);
  const totalOptionCount = useMemo(() => {
    if (allOptionCount !== undefined) return allOptionCount;
    if (!optionCounts) return null;
    return options.reduce((total, option) => total + (optionCounts[option] ?? 0), 0);
  }, [allOptionCount, optionCounts, options]);

  const searchPlaceholderLabel = searchLabel || (typeof label === 'string' ? label.toLowerCase() : 'options');

  return (
    <div
      className={layout === 'inline' ? 'grid items-center gap-2 sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:gap-3' : 'space-y-2'}
      ref={containerRef}
    >
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative h-12">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-haspopup="true"
          className={`flex h-full w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-left text-sm shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
            disabled ? 'bg-gray-50 text-gray-400' : ''
          }`}
        >
          <span className="truncate text-gray-700">{displayText}</span>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open && !disabled && (
          <div
            id={menuId}
            onKeyDown={handleMenuKeyDown}
            className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-2 text-sm shadow-xl"
          >
            {searchable && (
              <div className="px-4 pb-2">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={`Search ${searchPlaceholderLabel}`}
                  aria-label={`Search ${searchPlaceholderLabel}`}
                  className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            )}
            {showAllOption && (
              <>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  onClick={clearValues}
                  aria-pressed={values.length === 0}
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border border-gray-300 ${
                      values.length === 0 ? 'bg-blue-100 border-blue-300' : 'bg-white'
                    }`}
                  >
                  </span>
                  <span>All</span>
                  {totalOptionCount !== null && (
                    <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium tabular-nums text-slate-500">
                      {totalOptionCount.toLocaleString()}
                    </span>
                  )}
                </button>
                <div className="mt-1 border-t border-gray-100" />
              </>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-xs text-slate-500">No matches</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.has(option);
                const isDisabled = disabledOptionSet.has(option);
                return (
                  <label
                    key={option}
                    className={`flex items-center gap-3 px-4 py-2 ${
                      isDisabled
                        ? 'cursor-not-allowed text-slate-400'
                        : 'cursor-pointer text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                    } ${
                      isSelected ? 'text-blue-700' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleValue(option)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option}</span>
                      {optionDescriptions?.[option] && (
                        <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                          {optionDescriptions[option]}
                        </span>
                      )}
                    </span>
                    {optionCounts && (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${
                        isDisabled ? 'bg-slate-50 text-slate-400' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {(optionCounts[option] ?? 0).toLocaleString()}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
